import { useLocation } from '@reach/router';
import {
  LessonPlanEquipment,
  LessonsAccess,
  LessonsActivity,
  LessonsApi,
  LessonsQuery,
  LessonsResult,
  Subject,
  SubjectContents,
} from 'api/lessonsApi';
import { AppPath } from 'appConstants';
import ResourceAudioImage from 'images/resources/Img_Resource_Audio.png';
import ResourceDefaultImage from 'images/resources/Img_Resource_Default.png';
import ResourceVideoImage from 'images/resources/Img_Resource_Video.png';
import ResourceDocumentImage from 'images/lessons/Img_Resource_Document.png';
import ActivityDefaultImage from 'images/subjectSite/Card_Default.png';
import EquipmentDefaultImage from 'images/subjectSite/Img_Equipment_Default.png';
import PresentationImage from 'images/subjectSite/Full_Screen_Icon.png';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

export type LessonsRouteParams = {
  subject?: string;
  keyStage?: string;
  activity?: string;
  lessonNumber?: string;
};

export type LessonsPageData<T> = {
  subject: Subject;
  labels: SubjectContents;
  query: LessonsQuery;
  details: T;
};

const YEARS_PATTERN = /^\d+(,\d+)*$/;
const LESSON_NUMBER_PATTERN = /^\d{1,9}$/;

export const unitPath = (
  subjectCode: string,
  keyStage: string,
  activity: string
) =>
  `/${subjectCode.toLowerCase()}/lesson/${keyStage.toLowerCase()}/${activity.toLowerCase()}`;

export const splitLines = (text: string | null) =>
  (text ?? '').split(/\r?\n/).filter((line) => line.trim() !== '');

/**
 * Loads a lesson page's subject, labels and details from its path and ?years=.
 * A bad path, an unknown subject or a refused call is 'notFound'.
 */
export const useLessonsPage = <T>(
  params: LessonsRouteParams,
  load: (query: LessonsQuery, lessonNumber: string) => Promise<LessonsResult<T>>
): LessonsResult<LessonsPageData<T>> | null => {
  const { search } = useLocation();
  const [result, setResult] = useState<LessonsResult<
    LessonsPageData<T>
  > | null>(null);
  const yearsParam = queryString.parse(search).years;
  const years = typeof yearsParam === 'string' ? yearsParam : '';
  const { subject: code, keyStage, activity, lessonNumber } = params;

  useEffect(() => {
    let bCurrent = true;
    const done = (value: LessonsResult<LessonsPageData<T>>) => {
      if (bCurrent) setResult(value);
    };

    setResult(null);
    if (
      !code ||
      !keyStage ||
      !activity ||
      !YEARS_PATTERN.test(years) ||
      (lessonNumber !== undefined && !LESSON_NUMBER_PATTERN.test(lessonNumber))
    ) {
      done('notFound');
      return;
    }

    (async () => {
      const subject = await LessonsApi.getSubjectByCode(code);
      if (typeof subject === 'string') return done(subject);

      const query = {
        subjectsId: subject.subjectsID,
        keyStage,
        activity,
        years,
      };
      const [details, labels] = await Promise.all([
        load(query, lessonNumber ?? ''),
        LessonsApi.getSubjectContents(subject.subjectsID),
      ]);
      if (typeof details === 'string') return done(details);

      done({ subject, labels, query, details });
    })();

    return () => {
      bCurrent = false;
    };
    // load is a fixed api call per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, keyStage, activity, lessonNumber, years]);

  return result;
};

export type AccessButton = {
  text: string;
  href: string | null;
  bDisabled: boolean;
  /**
   * The subject site's classes for the button
   */
  className: string;
};

/**
 * The button for an item the school user can open
 */
export const openAccessButton = (
  text: string,
  href: string | null
): AccessButton => ({
  text,
  href,
  bDisabled: false,
  className: 'BtnTwo BtnView',
});

export const SUBSCRIBERS_ONLY_BUTTON: AccessButton = {
  text: 'Subscribers Only',
  href: null,
  bDisabled: true,
  className: 'BtnDisabled BtnView',
};

/**
 * The button for an item the school user cannot open, by why they cannot
 */
export const closedAccessButton = (
  access: Pick<LessonsAccess, 'status'>
): AccessButton => {
  switch (access.status) {
    case 'AccountPaused':
      return {
        text: 'account paused',
        href: null,
        bDisabled: true,
        className: 'BtnDisabled BtnView',
      };
    case 'CompleteSubscriptionPayment':
      return {
        text: 'Complete Subscription Payment',
        href: AppPath.UPGRADE_SUBSCRIPTION,
        bDisabled: false,
        className: 'BtnOne BtnSubscribe BtnComplete',
      };
    case 'UpgradePackage':
      return {
        text: 'Upgrade Package',
        href: AppPath.DASHBOARD,
        bDisabled: false,
        className: 'BtnOne BtnSubscribe',
      };
    default:
      return SUBSCRIBERS_ONLY_BUTTON;
  }
};

/**
 * The activity's picture for a lesson page hero, or the subject site's default card picture
 */
export const activityImage = (activity: LessonsActivity) =>
  activity.imagePath || ActivityDefaultImage;

export const PRESENTATION_IMAGE = PresentationImage;

/**
 * A lesson resource's picture, by its resource type
 */
export const resourceTypeImage = (resourceType: string) => {
  switch (resourceType) {
    case 'Video':
      return ResourceVideoImage;
    case 'Document':
      return ResourceDocumentImage;
    case 'Audio':
      return ResourceAudioImage;
    case 'Presentation':
      return PresentationImage;
    default:
      return ResourceDefaultImage;
  }
};

/**
 * A lesson equipment item's picture: the resource card picture for a resource card, else its own
 * or the default equipment picture
 */
export const equipmentImage = (item: LessonPlanEquipment) =>
  item.bIsResourceCard
    ? ResourceDocumentImage
    : item.imagePath || EquipmentDefaultImage;

/**
 * Text made safe to place in HTML, as content or a quoted attribute
 */
export const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Opens a file link in a new tab. The tab opens before the link is fetched, as one opened after
 * an api call is blocked as a popup.
 * @returns false when the link could not be fetched
 */
export const openFileInNewTab = async (
  getUrl: () => Promise<string | null>,
  fileWindow: Window | null = window.open('', '_blank')
) => {
  const url = await getUrl();
  if (url && fileWindow) {
    fileWindow.location.href = url;
    return true;
  }
  fileWindow?.close();
  return false;
};

/**
 * Downloads a file link in place: the link is served as an attachment, so following it from a
 * hidden anchor saves the file without leaving the page or opening a tab.
 * @returns false when the link could not be fetched
 */
export const downloadFileInPlace = async (
  getUrl: () => Promise<string | null>
) => {
  const url = await getUrl();
  if (!url) {
    return false;
  }
  const link = document.createElement('a');
  link.href = url;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
};
